import { reactive, nextTick, ref, Ref } from '@vue/composition-api';
import EmergencyResponseTeamCarApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_car';
import {
  CarInfo,
  CarState,
  SearchState,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMap';
import { parseDateTimeString } from '@/lib/dateTimeUtil';
import {
  createInitialCarsData,
  convData,
  shouldShowCar,
  deselectReportItems,
  loadEmergencyResponseTeamMtxs,
} from '@/components/ExternalReports/EmergencyResponseTeam/utils';
import { createInjection } from '@/lib/createInjection';
import { IntervalID } from '@/lib/requestAnimationFrame';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import { EmergencyResponseTeamCarExt } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamCar';

const RECENT_DAYS = 60;

interface InjectedState {
  carState: CarState;
  searchState: SearchState;
}
export interface UseEmergencyResponseTeamCarsResult {
  carState: CarState;
  startCarUpdateInterval: (userSettings: ExternalReportSettings) => Promise<void>;
  stopCarUpdateInterval: () => void;
  filterCars: () => void;
  getSelectedCar: () => EmergencyResponseTeamCarExt | null;
  selectCar: (carId: number) => Promise<void>;
  deselectCar: (carId: number) => boolean;
  deselectSelectedCarReportPhotos: () => void;
  clearCars: () => void;
  redrawCarLayerWatchValue: Ref<number>;
  notifyRedrawCarLayerNeeded: () => void;
}

const { provide, inject } = createInjection<InjectedState>('emergencyResponseTeam/useEmergencyResponseTeamCars');

export function provideEmergencyResponseTeamCars(searchState: SearchState): InjectedState {
  const carState = reactive<CarState>({
    cars: [],
    carsUpdatedAt: new Date(),
  });
  const injectedState = { carState, searchState };
  provide(injectedState);
  return injectedState;
}

export default function useEmergencyResponseTeamCars(defaultInjectedState?: InjectedState): UseEmergencyResponseTeamCarsResult {
  const injectedState = defaultInjectedState ?? inject();
  const carState = injectedState.carState;
  const searchState = injectedState.searchState;
  let carUpdateTimer: IntervalID | null = null;
  let isRefreshing = false;
  const redrawCarLayerWatchValue = ref<number>(0);

  const startCarUpdateInterval = async(userSettings: ExternalReportSettings) => {
    window.clearRequestInterval(carUpdateTimer);

    const refreshSafely = async() => {
      if (isRefreshing) return;
      isRefreshing = true;
      try {
        await refreshCars(userSettings);
      } finally {
        isRefreshing = false;
      }
    };

    await refreshSafely();
    carUpdateTimer = window.requestInterval(() => {
      void refreshSafely();
    }, 10000);
  };
  const stopCarUpdateInterval = () => {
    window.clearRequestInterval(carUpdateTimer);
    carUpdateTimer = null;
  };

  const notifyRedrawCarLayerNeeded = () => {
    redrawCarLayerWatchValue.value += 1;
  };

  const filterCars = () => {
    // 検索ボタンが押された時に、検索条件を確定する
    searchState.decidedSearchConds = { ...searchState.searchConds };
    carState.cars.forEach(car => {
      car.shouldShow = shouldShowCar(car, searchState.decidedSearchConds);
    });
  };

  const refreshCars = async(userSettings: ExternalReportSettings) => {
    const emergencyResponseTeamCars = await EmergencyResponseTeamCarApi.getEmergencyResponseTeamCars();
    const statusOrder = [
      userSettings.choices.status.working.key,
      userSettings.choices.status.stopped.key,
    ];
    const findIndex = (status: string) => {
      const index = statusOrder.indexOf(status);
      return index === -1 ? statusOrder.length : index;
    };
    emergencyResponseTeamCars.sort((a, b) => {
      const indexA = findIndex(a.status);
      const indexB = findIndex(b.status);
      return indexA - indexB;
    });
    const recentTs = new Date().setDate(new Date().getDate() - RECENT_DAYS);
    const recentData = emergencyResponseTeamCars.filter(emergencyResponseTeamCar => {
      try {
        const tsDate = parseDateTimeString(emergencyResponseTeamCar.ts);
        return tsDate.getTime() > recentTs;
      } catch (e) {
        return false;
      }
    });

    // carState.carsが空の場合は固定値、2回目以降は自分で持ってるやつ
    const isInitial = carState.cars.length === 0;
    if (isInitial) {
      carState.cars = createInitialCarsData(recentData);
    }
    const currentCarInfoMap = carState.cars.reduce((acc: Record<number, CarInfo>, e) => {
      acc[e.id] = {
        isSelected: e.isSelected,
        selectedPhotoId: e.reportExt?.photosExt.find(photo => photo.isSelected)?.id ?? -1,
        selectedMtxTs: e.reportExt?.mtxsExt.find(mtx => mtx.isSelected)?.ts ?? '',
        savedImageMap: e.reportExt?.photosExt.reduce((acc2: Record<number, string>, photo) => {
          acc2[photo.id] = photo.savedImage || '';
          return acc2;
        }, {}) ?? {},
        loadedMtxs: e.reportExt?.mtxsExt ?? [],
      };
      return acc;
    }, {});

    carState.cars = convData({
      cars: recentData,
      currentCarInfoMap,
      userSettings,
    });
    filterCars();
    carState.carsUpdatedAt = new Date();
    nextTick(() => {
      notifyRedrawCarLayerNeeded();
    });
  };

  const getSelectedCar = (): EmergencyResponseTeamCarExt | null => {
    return carState.cars.find(e => e.isSelected) ?? null;
  };

  const selectCar = async(carId: number): Promise<void> => {
    const prev = getSelectedCar();
    if (prev && prev.id !== carId && prev.reportExt) {
      deselectReportItems(prev.reportExt);
    }

    for (const car of carState.cars) {
      car.isSelected = car.id === carId;
      if (!car.isSelected) continue;

      const report = car.reportExt;
      if (!report) continue;

      if (!report.isMtxsLoaded) {
        await loadEmergencyResponseTeamMtxs([report]);
      }
    }
  };

  const deselectCar = (carId: number): boolean => {
    const car = getSelectedCar();
    if (!car || car.id !== carId) return false;

    car.isSelected = false;
    if (car.reportExt) {
      deselectReportItems(car.reportExt);
    }
    return true;
  };

  const deselectSelectedCarReportPhotos = (): void => {
    const car = getSelectedCar();
    if (!car || !car.reportExt) return;

    deselectReportItems(car.reportExt);
  };

  const clearCars = (): void => {
    carState.cars.forEach(car => {
      if (!car.reportExt) return;

      for (const photo of car.reportExt.photosExt) {
        if (photo.savedImage) {
          URL.revokeObjectURL(photo.savedImage);
        }
      }
    });
    carState.cars = [];
  };

  return {
    carState,
    startCarUpdateInterval,
    stopCarUpdateInterval,
    filterCars,
    getSelectedCar,
    selectCar,
    deselectCar,
    deselectSelectedCarReportPhotos,
    clearCars,
    redrawCarLayerWatchValue,
    notifyRedrawCarLayerNeeded,
  };
}
